import * as moment from 'moment';
import * as _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { IMicrositeRecipientForList, micrositeListTypeMap } from '@nkr/models/index';
import { MicrositeListType, MicrositeRecipientStatus, OrganType } from '@nkr/enums/index';
import { ActionButtonRenderer } from './action-button-renderer';
import { AgGridReact } from 'ag-grid-react';
import { BodyScrollEvent, ColDef, FirstDataRenderedEvent, GridColumnsChangedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { MicrositeAction } from '../microsite-action';
import { Glyphicon, Label } from 'react-bootstrap';
import { PostTransplantBannerStatusRenderer, RowNumberRenderer, StatusRenderer } from './renderers';
import { Utility } from '@nkr/services/utility';
import { CustomSiteStatus } from '@nkr/enums/microsite-recipient-status';
import { IMicrositeRecipientForListWithSelect } from './models/microsite-recipient-for-list-with-select';
import { useAppHookContext } from '@nkr/utils/rest-api';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import CheckboxRenderer from '@nkr/components/checkbox-renderer';
import CoachCell from './coach-cell';


interface IProps {
    list: IMicrositeRecipientForListWithSelect[],
    listType: MicrositeListType,
    centerId: number,
    isAdmin: boolean,
    isAdminLogin: boolean,
    isLoading: boolean,
    onSelectAllRows: () => void,
    onRowSelected: (id: number, isSelected: boolean) => void,
    onSendReminderEmail: (id: number) => void,
    onGetDonorRegistrationForMSPatients: (id: number) => void,
    onResendApprovalEmail: (id: number) => void,
    onDownloadBatchFile: (id: number) => void,
    onShowLinkedEntities: (id: number) => void,
    onLoadingComplete: () => void,
    onGridReady: (e: GridReadyEvent) => void,
    onChangeStatus: (id: number) => void
}

const CommonList = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);
    const { app: { site } } = useAppHookContext();

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
        props.onGridReady(e);
    }

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onBodyScroll = (e: BodyScrollEvent) => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    }

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const handleItemSelected = (id: number, action: MicrositeAction) => {
        switch (action) {
            case MicrositeAction.ChangeStatus:
                props.onChangeStatus(id);
                break;
            case MicrositeAction.SendReminderEmail:
                props.onSendReminderEmail(id);
                break;

            case MicrositeAction.ResendApprovalEmail:
                props.onResendApprovalEmail(id);
                break;

            case MicrositeAction.DownloadCardBatchFile:
                props.onDownloadBatchFile(id);
                break;

            case MicrositeAction.ViewRegistrations:
            case MicrositeAction.ViewRegistrationsNoCenter:
                props.onGetDonorRegistrationForMSPatients(id);
                break;

            case MicrositeAction.ViewLinkedEntities:
                props.onShowLinkedEntities(id)
                break;
        }
    };

    const handleRowSelected = (id: number, isSelected: boolean) => {
        props.onRowSelected(id, isSelected);
    };

    const handleSelectAllRows = () => {
        props.onSelectAllRows();
    };

    const getStatusColumnWidth = () => {
        if (props.listType == MicrositeListType.PendingPatient) {
            return 150;
        }

        if (props.listType == MicrositeListType.PendingConfirmation || props.listType == MicrositeListType.PendingCenter || props.listType == MicrositeListType.PendingNkr) {
            return 175;
        }

        if (props.listType == MicrositeListType.ActiveStarterSites) {
            return 170;
        }

        if (props.listType == MicrositeListType.ActiveCustomSites) {
            return 160;
        }

        if (props.listType == MicrositeListType.Disapproved) {
            return 120;
        }

        if (props.listType == MicrositeListType.Inactive) {
            return 210;
        }

        return 270;
    };

    const getListTypeName = () => {
        let listType = micrositeListTypeMap.find(x => x.type == props.listType);

        if (listType != null) {
            return listType.name;
        }

        return '';
    };

    const list = useMemo(() => props.list.length > 0 ? props.list : (props.isLoading ? null : []), [props.list]);

    const showSubmissionDateTime =
        props.listType == MicrositeListType.All
        || props.listType == MicrositeListType.PendingCenter
        || props.listType == MicrositeListType.PendingNkr
        || props.listType == MicrositeListType.ActiveCustomSites;

    const showPatientRegistrationDateTime =
        props.listType == MicrositeListType.All
        || props.listType == MicrositeListType.PendingConfirmation;

    const showCenterDecisionDateTime = props.listType == null || props.listType == MicrositeListType.PendingNkr;
    const showNkrApprovedDateTime = props.listType == null || props.listType == MicrositeListType.ActiveCustomSites;
    const showNkrDisapprovedDateTime = props.listType == null || props.listType == MicrositeListType.Disapproved;
    const statusColumnWidth = getStatusColumnWidth();
    const listTypeName = getListTypeName();

    const columns: ColDef[] = [
        {
            colId: 'isSelected',
            field: 'isSelected',
            headerName: '',
            width: 40,
            sortable: false,
            suppressSizeToFit: true, headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><Glyphicon glyph='check' title='Check/Uncheck All Rows' onClick={handleSelectAllRows} /></SortableHeaderComponent>,
            cellRendererParams: { onValueChange: handleRowSelected },
            cellRenderer: 'checkboxRenderer'
        },
        {
            colId: 'rowNumber',
            headerName: '#',

            width: 60,
            minWidth: 60,
            sortable: false,
            suppressSizeToFit: true,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>#</SortableHeaderComponent>,
            cellRendererFramework: RowNumberRenderer,
        },
        {
            colId: 'firstName',
            field: 'firstName',
            headerName: 'First Name',
            width: 140,
            comparator: Utility.nameComparator,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>First Name</SortableHeaderComponent>,
        },
        {
            colId: 'lastName',
            field: 'lastName',
            headerName: 'Last Name',
            width: 140,
            comparator: Utility.nameComparator,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Name</SortableHeaderComponent>,
        },
        {
            colId: 'languagePreference',
            field: 'languagePreference',
            headerName: 'Language Preference',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Lang.<br />Pref.</SortableHeaderComponent>
        },
        {
            colId: 'birthdate',
            field: 'birthdate',
            headerName: 'Date of Birth',
            width: 130,
            comparator: Utility.dateComparator,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date of Birth</SortableHeaderComponent>
        },
        {
            colId: 'centerAlias',
            field: 'center.alias',
            headerName: 'Center',
            width: 130,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IMicrositeRecipientForList

                if (data.center == null) {
                    return <Label>- NO CENTER -</Label>
                }

                return <>{params.value}</>
            }
        },
        {
            colId: 'coordinatorContactName',
            field: 'coordinatorContactName',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Primary<br />Coordinator</SortableHeaderComponent>,
        },
        {
            colId: 'campaignCode',
            field: 'campaignCode',
            headerName: 'Campaign',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Campaign</SortableHeaderComponent>,
        },
        {
            colId: 'coachContactName',
            field: 'coachContactName',
            headerName: 'Coach',
            width: 270,
            cellClass: `microsite-list-cell coach-drop-cell`,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Coach</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                return <CoachCell recipient={params.data} isAdmin={props.isAdmin || props.isAdminLogin} selectorSize='xs' />;
            }
        },
        {
            colId: 'inviteSentDateTime',
            field: 'inviteSentDateTime',
            headerName: 'Invite Sent Date/Time',
            width: 105,
            hide: showPatientRegistrationDateTime,
            suppressSizeToFit: true,
            comparator: Utility.dateComparator,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Invite Sent<br />Date/Time</SortableHeaderComponent>,
        },
        {
            colId: 'patientRegistrationDateTime',
            field: 'patientRegistrationDateTime',
            headerName: 'Patient Registration Date/Time',
            width: 105,
            hide: !showPatientRegistrationDateTime,
            suppressSizeToFit: true,
            comparator: Utility.dateComparator,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Patient Reg.<br />Date/Time</SortableHeaderComponent>,
        },
        {
            colId: 'submissionDateTime',
            field: 'submissionDateTime',
            headerName: 'Date Site Submitted',
            width: 105,
            hide: !showSubmissionDateTime,
            suppressSizeToFit: true,
            comparator: Utility.dateComparator,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date Site<br />Submitted</SortableHeaderComponent>,
        },
        {
            colId: 'centerDecisionDateTime',
            field: 'centerDecisionDateTime',
            headerName: 'Date Center Approved',
            width: 105,
            hide: !showCenterDecisionDateTime,
            suppressSizeToFit: true,
            comparator: Utility.dateComparator,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date Center<br />Approved</SortableHeaderComponent>,
        },
        {
            colId: 'dateNkrApproved',
            field: 'nkrDecisionDateTime',
            headerName: 'Date NKR Approved',
            width: 95,
            hide: !showNkrApprovedDateTime,
            suppressSizeToFit: true,
            comparator: Utility.dateComparator,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date NKR<br />Approved</SortableHeaderComponent>,
        },
        {
            colId: 'dateSiteDisapproved',
            field: 'nkrDecisionDateTime',
            headerName: 'Date Site Disapproved',
            width: 105,
            hide: !showNkrDisapprovedDateTime,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date Site<br />Disapproved</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IMicrositeRecipientForList;

                if (data.currentStatusId == MicrositeRecipientStatus.Denied || data.customSiteStatusId == CustomSiteStatus.DeclinedUnfixable) {
                    if (data.nkrDecisionDateTime != '') {
                        return <>{data.nkrDecisionDateTime}</>;
                    }

                    if (data.centerDecisionDateTime != '') {
                        return <>{data.centerDecisionDateTime}</>;
                    }

                    if (data.submissionDateTime != '') {
                        return <>{data.submissionDateTime}</>;
                    }

                    return <>{data.inviteSentDateTime}</>;
                }

                return <>--</>;
            },
            suppressSizeToFit: true,
            comparator: Utility.dateComparator
        },
        {
            colId: 'reasonForDisapproval',
            field: 'submissionLastIssue',
            headerName: 'Reason for Disapproval',
            width: 300,
            hide: !showNkrDisapprovedDateTime,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Reason for<br />Disapproval</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IMicrositeRecipientForList;

                if (data.logs.length > 0) {
                    let logs = _.orderBy(data.logs, (item) => {
                        return moment(item.logDateTime, 'MM/DD/YYYY @ HH:mm').toDate();
                    }, ['desc'])

                    let recentEntry = logs[0];

                    return <div style={{ fontSize: 12 }}>{recentEntry.logEntry}</div>;
                }

                return <div style={{ fontSize: 12 }}>{data.submissionLastIssue}</div>;
            }
        },
        {
            colId: 'campaignCode',
            field: 'campaignCode',
            headerName: 'Campaign (Key)',
            width: 170,
            hide: props.listType != MicrositeListType.PendingConfirmation,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Campaign (Key)</SortableHeaderComponent>,
        },
        {
            colId: 'registrationCount',
            field: 'registrationCount',
            headerName: 'Registrations',
            width: 70,
            hide: props.listType != MicrositeListType.ActiveStarterSites && props.listType != MicrositeListType.ActiveCustomSites && props.listType != MicrositeListType.All,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>DASH<br />Reg.</SortableHeaderComponent>,
        },
        {
            colId: 'currentStatus',
            field: 'currentStatusDescription',
            headerName: 'Status',
            width: statusColumnWidth,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
            cellRendererFramework: StatusRenderer,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IMicrositeRecipientForList;
                let pendApprovalStatus = [MicrositeRecipientStatus.StarterSiteActive, MicrositeRecipientStatus.Active] as const;
                let pendingApproval = pendApprovalStatus.includes(data.currentStatusId) && data.customSiteStatusId > 0;

                if (pendingApproval) {
                    return data.customSiteStatusDescription;
                }

                return data.currentStatusDescription;
            }
        },
        {
            colId: 'micrositeBannerStatusDesc',
            field: 'micrositeBannerStatusDesc',
            headerName: 'micrositeBannerStatusDesc',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Post Txp<br />Banner Active</SortableHeaderComponent>,
            cellRendererFramework: PostTransplantBannerStatusRenderer
        },
        {
            colId: 'donorOrigin',
            field: 'donorOriginDescription',
            headerName: 'Donor Origin',
            width: 150,
            hide: props.listType != MicrositeListType.Inactive,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Origin</SortableHeaderComponent>,
        },
        {
            colId: 'isAddressVerified',
            field: 'isAddressVerified',
            headerName: 'Address Verified',
            width: 150,
            hide: props.listType != MicrositeListType.PendingCards,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Address Verified</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IMicrositeRecipientForList;

                if (data.isAddressVerified != null) {
                    if (data.isAddressVerified == true) {
                        return (
                            <Glyphicon glyph='ok' style={{ color: 'green' }} />
                        );
                    } else {
                        return (
                            <Glyphicon glyph='remove' style={{ color: 'red' }} />
                        );
                    }
                }

                return <>--</>;
            },
        },
        {
            colId: 'micrositeType',
            field: 'micrositeType',
            headerName: 'Type',
            width: 90,
            hide: site.organType == OrganType.Liver,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type</SortableHeaderComponent>,
        },
        {
            colId: 'actionMenu',
            width: 100,
            cellClass: 'action-menu',
            suppressSizeToFit: true,
            sortable: false,
            cellRendererParams: { pullRight: true, listType: props.listType, isAdmin: props.isAdmin, isAdminLogin: props.isAdminLogin, onMenuToggle: handleMenuToggle, onItemSelected: handleItemSelected },
            cellRendererFramework: ActionButtonRenderer,
        }
    ];

    const columnDefs = useMemo(() => columns, [props.list]);

    let overlayLoadMsg = `Loading [${listTypeName}] microsites...`;

    const queryParams = new URLSearchParams(window.location.search);

    if (queryParams.get('msId') != null) {
        overlayLoadMsg = 'Loading Selected Microsite, please wait...';
    }

    return (
        <AgGridReact
            defaultColDef={{
                cellClass: 'microsite-list-cell',
                headerClass: 'microsite-list-header',
                sortable: true
            }}
            columnDefs={columnDefs}
            rowData={list}
            headerHeight={45}
            groupHeaderHeight={30}
            rowHeight={28}
            enableCellTextSelection={true}
            enableRangeSelection={true}
            immutableData={true}
            overlayNoRowsTemplate={`No [${listTypeName}] microsites...`}
            overlayLoadingTemplate={overlayLoadMsg}
            getRowNodeId={(data: IMicrositeRecipientForListWithSelect) => data.id.toString()}
            frameworkComponents={{ checkboxRenderer: CheckboxRenderer }}
            suppressContextMenu={true}
            onGridReady={onGridReady}
            onFirstDataRendered={onFirstDataRendered}
            onBodyScroll={onBodyScroll}
            onSortChanged={(e: SortChangedEvent) => { e.api.refreshCells(); }}
            onGridSizeChanged={onGridSizeChanged}
            onGridColumnsChanged={(e: GridColumnsChangedEvent) => e.api.sizeColumnsToFit()}
        />
    );
}

export default CommonList;
